import { useCallback } from 'react';
import useAppContext from '../../../hooks/useAppContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useSendToClock, {
  SendableContentKind,
} from '../../../hooks/useSendToClock';
import useSentToClockMarker from '../../../hooks/useSentToClockMarker';
import useSendKlydoToClockMutation from './useSendKlydoToClockMutation';
import { AlertType } from '../../../design-system/DSAlert';
import { Klydo } from '../../../api/klydo';

const useSendKlydoToClock = (
  klydo: Pick<Klydo, 'id' | 'subtitle' | 'capabilitiesV2'> | undefined,
) => {
  const { clock, showAlert } = useAppContext();
  const { trackEvent } = useAnalytics();
  const { isSent, markAsSent } = useSentToClockMarker(clock, klydo?.id);

  const { mutate: sendKlydo, isPending } = useSendKlydoToClockMutation({
    onSuccess: () => {
      markAsSent();
      // Tracked here, not on click, so the metric counts delivered sends rather
      // than attempts the API rejected.
      if (klydo) {
        trackEvent({
          type: 'klydo_sent_to_clock',
          context: `klydoId:${klydo.id}`,
        });
      }
      showAlert({
        message: 'Your klydo is on its way to your clock!',
        alertType: AlertType.SUCCESS,
      });
    },
  });

  const send = useCallback(() => {
    if (!klydo) return;
    sendKlydo({ klydoId: klydo.id });
  }, [klydo, sendKlydo]);

  return useSendToClock({
    kind: SendableContentKind.Klydo,
    hasContent: !!klydo,
    klydos: klydo ? [klydo] : [],
    sentMarker: isSent,
    send,
    isSending: isPending,
  });
};

export default useSendKlydoToClock;
